import { ArrowRight } from 'lucide-react';

import { motion } from '@/components/public/PublicMotion';
import { SectionEyebrow } from '@/components/public/SectionEyebrow';
import { Heading } from '@/components/ui/heading';
import { fadeInUp, staggerContainer } from '@/hooks/use-scroll-animation';
import { formatBlogCardDate } from '@/lib/formatters';

type BlogSectionPost = {
  id: number;
  title: string;
  slug: string;
  caption?: string | null;
  publishedAt?: string | null;
  ogImage?: { url: string } | null;
};

type BlogSectionProps = {
  posts?: BlogSectionPost[];
};

const placeholderTones = [
  'bg-blue-light text-primary-dark',
  'bg-green-light text-green-dark',
  'bg-purple-light text-purple-dark',
] as const;

export function BlogSection({ posts }: BlogSectionProps) {
  if (!posts || posts.length === 0) return null;

  return (
    <section id="blog" className="bg-background py-20 md:py-24">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
          transition={{ duration: 0.55 }}
          className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between"
        >
          <div className="grid gap-3">
            <SectionEyebrow className="text-primary">Dal blog</SectionEyebrow>
            <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
              IA e pratica clinica, senza fumo
            </Heading>
          </div>
          <a
            href="/blog"
            className="group flex shrink-0 items-center gap-1.5 font-semibold text-primary no-underline hover:text-primary-dark"
          >
            Vai al blog
            <ArrowRight
              aria-hidden
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
            />
          </a>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="grid gap-6 md:grid-cols-3"
        >
          {posts.map((post, index) => (
            <motion.a
              key={post.id}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              href={`/blog/${post.slug}`}
              className="block overflow-hidden rounded-lg border border-border bg-card text-inherit no-underline shadow-card transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg"
            >
              {post.ogImage?.url ? (
                <img
                  src={post.ogImage.url}
                  alt=""
                  className="h-[150px] w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div
                  aria-hidden
                  className={`flex h-[150px] items-center justify-center text-xs ${placeholderTones[index % placeholderTones.length]}`}
                >
                  Emovia
                </div>
              )}
              <div className="grid gap-2.5 p-5">
                {post.publishedAt && (
                  <p className="text-xs text-muted-foreground">
                    {formatBlogCardDate(post.publishedAt)}
                  </p>
                )}
                <Heading level={3} className="text-base font-bold leading-snug text-primary-dark">
                  {post.title}
                </Heading>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
